/**
 * Client-side permission / feature checks aligned with JWT claims.
 * Server mirrors filtering in resolve_effective_permissions + auth_hook_custom_claims.
 */

export function permissionFeatureKey(permission: string): string {
  const parts = permission.split('.')
  if (parts[0] === 'hub' && parts[1]) {
    return parts[1]
  }
  return parts[0] ?? permission
}

export type PermissionCheckOptions = {
  permissions: string[]
  features: string[]
  isOwner: boolean
  /**
   * When true (default), skip the feature gate if JWT has no features[] yet
   * (stale session before refresh). Server always filters on new tokens.
   */
  failOpenWithoutFeatures?: boolean
}

export function checkPermission(
  permission: string | boolean | undefined,
  options: PermissionCheckOptions
): boolean {
  if (typeof permission === 'boolean') return permission
  if (!permission) return false

  const {
    permissions,
    features,
    isOwner,
    failOpenWithoutFeatures = true
  } = options

  const featureKey = permissionFeatureKey(permission)

  if (features.length > 0) {
    if (!features.includes(featureKey)) return false
  } else if (!failOpenWithoutFeatures) {
    return false
  }

  if (isOwner) return true
  return permissions.includes(permission)
}

export function checkFeature(
  feature: string | boolean | undefined,
  features: string[]
): boolean {
  if (typeof feature === 'boolean') return feature
  if (!feature) return false
  return features.includes(feature)
}
